"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entityGrowFastRule = void 0;
exports.isEntityGrowingFast = isEntityGrowingFast;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const common_1 = require("../../common");
const database_1 = require("../../database");
const databaseUtils_1 = require("../../databaseUtils");
const bytes_1 = require("../../helpers/bytes");
const date_1 = require("../../helpers/date");
const duration_1 = require("../../helpers/duration");
const percent_1 = require("../../helpers/percent");
const rule_1 = require("../rule");
const ruleId = 'entity-grow-fast';
const ruleName = 'fast growing entity';
const ruleDescription = 'entities with rows or bytes growing over the daily/monthly/yearly rate thresholds and a size over the thresholds also';
const CustomRuleConf = rule_1.RuleConf.extend({
    ignores: database_1.EntityId.array().optional(),
    minRows: zod_1.z.number(),
    minSize: bytes_1.Bytes,
    maxGrowthYearly: common_1.Percent,
    maxGrowthMonthly: common_1.Percent,
    maxGrowthDaily: common_1.Percent,
}).strict().describe('EntityGrowFastConf');
exports.entityGrowFastRule = {
    id: ruleId,
    name: ruleName,
    description: ruleDescription,
    conf: { level: rule_1.RuleLevel.enum.medium, minRows: 10000, minSize: 10 * bytes_1.Mo, maxGrowthYearly: 2, maxGrowthMonthly: 0.1, maxGrowthDaily: 0.01 },
    zConf: CustomRuleConf,
    analyze(conf, now, db, queries, history, reference) {
        const refIgnores = reference.map(r => r.entity).filter(utils_1.isNotUndefined);
        const ignores = refIgnores.concat(conf.ignores?.map(databaseUtils_1.entityRefFromId) || []);
        const historyEntitiesById = history.map(h => ({ report: h.report, date: h.date, entities: (0, utils_1.indexBy)(h.database.entities || [], databaseUtils_1.entityToId) }));
        return (db.entities || [])
            .filter(e => !ignores.some(i => (0, databaseUtils_1.entityRefSame)(i, (0, databaseUtils_1.entityToRef)(e))))
            .map(e => {
            const previous = historyEntitiesById.map(h => {
                const entity = h.entities[(0, databaseUtils_1.entityToId)(e)];
                return entity ? { report: h.report, date: h.date, entity } : undefined;
            }).filter(utils_1.isNotUndefined);
            return isEntityGrowingFast(now, e, previous, conf.minRows, conf.minSize, conf.maxGrowthYearly, conf.maxGrowthMonthly, conf.maxGrowthDaily);
        })
            .filter(utils_1.isNotUndefined)
            .map(r => {
            const diff = r.kind === 'size' ? (0, bytes_1.showBytes)(r.diff) : (0, utils_1.pluralize)(r.diff, 'row');
            const rate = r.period > duration_1.oneYear ? ` (${(0, percent_1.showPercent)(r.yearly)} yearly)` : r.period > duration_1.oneMonth ? ` (${(0, percent_1.showPercent)(r.monthly)} monthly)` : r.period > duration_1.oneDay ? ` (${(0, percent_1.showPercent)(r.daily)} daily)` : '';
            return {
                ruleId,
                ruleName,
                ruleLevel: conf.level,
                message: `Entity ${(0, databaseUtils_1.entityToId)(r.current)} has grown by ${(0, percent_1.showPercent)(r.growth)} (${diff}) since ${(0, date_1.showDate)(r.date)}${rate}.`,
                entity: (0, databaseUtils_1.entityToRef)(r.current),
                extra: (0, utils_1.removeUndefined)({
                    previousReport: r.report,
                    previousDate: new Date(r.date).toISOString(),
                    periodMillis: r.period,
                    growth: r.growth,
                    kind: r.kind,
                    previousIndexes: r.previous?.indexes?.length,
                    currentIndexes: r.current.indexes?.length,
                    previous: r.previous?.stats,
                    current: r.current.stats,
                })
            };
        });
    }
};
function isEntityGrowingFast(now, current, history, minRows, minSize, maxGrowthYearly, maxGrowthMonthly, maxGrowthDaily) {
    const { rows, size } = current.stats || {};
    if ((rows && rows > minRows) || (size && size > minSize)) {
        const fastGrowing = history.map(h => {
            const hRows = h.entity.stats?.rows;
            const hSize = h.entity.stats?.size;
            const gRows = rows && hRows && rows > hRows ? { kind: 'rows', value: rows - hRows, percent: (0, percent_1.computePercent)(hRows, rows) } : undefined;
            const gSize = size && hSize && size > hSize ? { kind: 'size', value: size - hSize, percent: (0, percent_1.computePercent)(hSize, size) } : undefined;
            const growth = gRows && gSize ? (gRows.percent > gSize.percent ? gRows : gSize) : gRows || gSize;
            if (growth) {
                const period = now - h.date;
                const daily = growth.percent / (period / duration_1.oneDay);
                const monthly = growth.percent / (period / duration_1.oneMonth);
                const yearly = growth.percent / (period / duration_1.oneYear);
                if ((period > duration_1.oneDay && daily > maxGrowthDaily) || (period > duration_1.oneMonth && monthly > maxGrowthMonthly) || (period > duration_1.oneYear && yearly > maxGrowthYearly)) {
                    return { date: h.date, report: h.report, current, previous: h.entity, kind: growth.kind, diff: growth.value, growth: growth.percent, period, daily, monthly, yearly };
                }
            }
            return undefined;
        }).filter(utils_1.isNotUndefined);
        return (0, utils_1.maxBy)(fastGrowing, g => g.monthly);
    }
    return undefined;
}
//# sourceMappingURL=entityGrowFast.js.map